// TableauPatTestModal — sign-in test against Tableau Cloud + optional save.
//
// Two-step flow:
//   1. User fills server / site / PAT name + secret, clicks "Test
//      connection". We call POST /api/tableau-cloud/test-pat which
//      does a real TSC sign-in + sign-out round-trip.
//   2. On success the user gets a green "signed in" banner and a
//      "Save profile" affordance — saving POSTs to /profiles which
//      encrypts the secret at rest. Cancelling without saving
//      consumes the PAT only for this run (caller wires it as a
//      one-off via TableauOutputsConfig.patOneOff).
//
// Rendered as a fixed-position overlay so it floats above the
// Configure wizard without disturbing layout. Uses the Configure
// page's existing button + form-field tokens.

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { TableauPatOneOff, TableauProfile } from "@/lib/types";

interface TableauPatTestModalProps {
  onClose: () => void;
  onUseOneOff: (creds: TableauPatOneOff) => void;
  onSavedProfile: (profile: TableauProfile) => void;
}

interface TestResult {
  ok: boolean;
  detail: string | null;
  user_name: string | null;
}

export function TableauPatTestModal({
  onClose,
  onUseOneOff,
  onSavedProfile,
}: TableauPatTestModalProps): JSX.Element {
  const qc = useQueryClient();
  const [serverUrl, setServerUrl] = useState("https://10ax.online.tableau.com");
  const [siteName, setSiteName] = useState("");
  const [patName, setPatName] = useState("");
  const [patSecret, setPatSecret] = useState("");
  const [label, setLabel] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [test, setTest] = useState<TestResult | null>(null);

  const creds = (): TableauPatOneOff => ({
    serverUrl: serverUrl.trim(),
    siteName: siteName.trim(),
    patName: patName.trim(),
    patSecret,
  });

  const isComplete =
    serverUrl.trim().length > 0
    && siteName.trim().length > 0
    && patName.trim().length > 0
    && patSecret.length > 0;

  const testMut = useMutation({
    mutationFn: () => api.testTableauPat(creds()),
    onSuccess: (r) => setTest(r),
    onError: (e) =>
      setTest({ ok: false, detail: (e as Error).message, user_name: null }),
  });

  const saveMut = useMutation({
    mutationFn: () =>
      api.createTableauProfile({
        label: label.trim() || `${siteName} (${patName})`,
        ...creds(),
        isDefault,
      }),
    onSuccess: (profile) => {
      qc.invalidateQueries({ queryKey: ["tableau-profiles"] });
      onSavedProfile(profile);
      onClose();
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,32,52,.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480,
          maxWidth: "92vw",
          background: "white",
          borderRadius: 14,
          boxShadow: "0 20px 50px -10px rgba(3,45,96,.4)",
          padding: 22,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "#dcefe2",
              color: "var(--teal)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
              fontWeight: 800,
            }}
          >
            ⛅
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>
              Tableau Cloud — Personal Access Token
            </div>
            <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 1 }}>
              We&rsquo;ll sign in once to validate. Save the profile to
              encrypt the secret at rest and reuse it on future runs.
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
          <Field label="Server URL" hint="e.g. https://10ax.online.tableau.com">
            <input
              value={serverUrl}
              onChange={(e) => {
                setServerUrl(e.target.value);
                setTest(null);
              }}
              style={inputStyle}
              placeholder="https://10ax.online.tableau.com"
            />
          </Field>
          <Field label="Site name" hint="The path segment, e.g. ‘qbranch-demo’">
            <input
              value={siteName}
              onChange={(e) => {
                setSiteName(e.target.value);
                setTest(null);
              }}
              style={inputStyle}
              placeholder="qbranch-demo"
            />
          </Field>
          <Field label="PAT name">
            <input
              value={patName}
              onChange={(e) => {
                setPatName(e.target.value);
                setTest(null);
              }}
              style={inputStyle}
              placeholder="Claude Code"
            />
          </Field>
          <Field label="PAT secret">
            <input
              type="password"
              value={patSecret}
              onChange={(e) => {
                setPatSecret(e.target.value);
                setTest(null);
              }}
              style={inputStyle}
              autoComplete="off"
              placeholder="••••••••••••"
            />
          </Field>
        </div>

        {/* Test result panel */}
        {test && (
          <div
            role="status"
            style={{
              marginTop: 14,
              padding: "10px 12px",
              borderRadius: 8,
              background: test.ok ? "rgba(76,175,80,.08)" : "rgba(239,83,80,.08)",
              border: `1px solid ${test.ok ? "rgba(76,175,80,.4)" : "rgba(239,83,80,.4)"}`,
              color: test.ok ? "#1b5e20" : "#b71c1c",
              fontSize: 12,
              lineHeight: 1.4,
            }}
          >
            {test.ok ? "✓ " : "✗ "}
            {test.detail ?? (test.ok ? "Signed in successfully." : "Sign-in failed.")}
            {test.ok && test.user_name ? ` Signed in as ${test.user_name}.` : ""}
          </div>
        )}

        {/* Save form (only after a successful test) */}
        {test?.ok && (
          <div
            style={{
              marginTop: 14,
              padding: 12,
              background: "var(--cb-98)",
              borderRadius: 10,
              display: "grid",
              gap: 10,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>
              Save this PAT for next time?
            </div>
            <Field label="Label">
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                style={inputStyle}
                placeholder={`${siteName} (${patName})`}
              />
            </Field>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                color: "var(--ink-2)",
              }}
            >
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              Set as default for new pipeline runs
            </label>
            {saveMut.isError && (
              <div style={{ fontSize: 11, color: "#b71c1c" }}>
                Save failed: {(saveMut.error as Error).message}
              </div>
            )}
          </div>
        )}

        {/* Footer buttons */}
        <div
          style={{
            marginTop: 18,
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <button className="btn ghost" onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className="btn"
            type="button"
            disabled={!isComplete || testMut.isPending}
            onClick={() => testMut.mutate()}
          >
            {testMut.isPending ? "Testing…" : "Test connection"}
          </button>
          {test?.ok && (
            <>
              <button
                className="btn ghost"
                type="button"
                onClick={() => {
                  onUseOneOff(creds());
                  onClose();
                }}
              >
                Use one-off (don&rsquo;t save)
              </button>
              <button
                className="btn primary"
                type="button"
                disabled={saveMut.isPending}
                onClick={() => saveMut.mutate()}
              >
                {saveMut.isPending ? "Saving…" : "Save & use"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 12,
  border: "1px solid var(--bd)",
  borderRadius: 6,
  background: "white",
  color: "var(--ink)",
  outline: "none",
};

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: ".06em",
          textTransform: "uppercase",
          color: "var(--ink-3)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
      {hint && (
        <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 3 }}>
          {hint}
        </div>
      )}
    </div>
  );
}
